import { IMeeting, ITranscriptInfo, TopicCounts } from "../context/AppStore";
import { DictionaryItem, DictionaryItems } from "../types";
import { findUniques, findUniquesByKey } from "./CollectionUtil";
import { TranscriptInfoInput, MeetingInput, CustomerInfo, getFullMeetingById_meeting_analytics_subtopic_count } from "../graphql/ql/operation-result-types";
export const COLORS = ["color1", "color2", "color3", "color4", "color5", "color6", "color7", "color8", "color9", "color10", "color11", "color12", "color13", "color14", "color15", "success-color-dark",
  "warning-color-dark", "info-color-dark", "primary-color-dark", "danger-color-dark",
  "secondary-color-dark", "default-color-dark", "purple-color-dark", "danger-color", "success-color", "info-color", "warning-color", "default-color", "primary-color", "secondary-color"];

/** Hex values matching SCSS variables in colors.scss for every CSS class name in COLORS. */
export const SPEAKER_COLOR_HEX: Record<string, string> = {
  color1: "#5c07d1",
  color2: "#F97316",
  color3: "#14bb7f",
  color4: "#6c1c88",
  color5: "#36b7d3",
  color6: "#298ce8",
  color7: "#362bb0",
  color8: "#bb6bd9",
  color9: "#dc47c4",
  color10: "#f68e6d",
  color11: "#63831e",
  color12: "#a8b700",
  color13: "#5094ba",
  color14: "#00695c",
  color15: "#4f37e3",
  "success-color-dark": "#007e33",
  "warning-color-dark": "#ff8800",
  "info-color-dark": "#0099cc",
  "primary-color-dark": "#0d47a1",
  "danger-color-dark": "#cc0000",
  "secondary-color-dark": "#9933cc",
  "default-color-dark": "#00695c",
  "purple-color-dark": "#4a148c",
  "danger-color": "#ff4444",
  "success-color": "#00C851",
  "info-color": "#33b5e5",
  "warning-color": "#ffbb33",
  "default-color": "#2bbbad",
  "primary-color": "#4285f4",
  "secondary-color": "#aa66cc",
};

/** Map a CSS color class name from `colors[speakerName]` to its hex equivalent. */
export function speakerClassToHex(cssClass: string | undefined): string | undefined {
  if (!cssClass) return undefined;
  return SPEAKER_COLOR_HEX[cssClass];
}

/**
 * Match an attendee (by name/email) to a transcript speaker name and return the
 * corresponding hex color from the shared speaker color map.
 * Returns undefined if no match is found, so callers can fall back.
 */
export function findSpeakerHexForAttendee(
  attendee: { name?: string | null; email?: string | null },
  speakerColors: DictionaryItem<string>,
): string | undefined {
  if (!attendee || !speakerColors) return undefined;
  const speakerNames = Object.keys(speakerColors);
  if (speakerNames.length === 0) return undefined;

  const norm = (s: string) => s.trim().toLowerCase();
  const firstAndLast = (s: string): { first: string; last: string } => {
    const parts = norm(s).split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first: "", last: "" };
    if (parts.length === 1) return { first: parts[0], last: "" };
    return { first: parts[0], last: parts[parts.length - 1] };
  };

  const attendeeName = attendee.name?.trim() || "";
  const emailLocal = attendee.email?.split("@")[0]?.trim() || "";

  // 1. Exact (case-insensitive) full name match
  if (attendeeName) {
    const target = norm(attendeeName);
    const match = speakerNames.find((s) => norm(s) === target);
    if (match) return speakerClassToHex(speakerColors[match]);
  }

  // 2. First + last name match (ignores middle words/initials,
  //    so "Mairin N Bheachain" matches "Mairin Ni Bheachain")
  if (attendeeName) {
    const a = firstAndLast(attendeeName);
    if (a.first && a.last) {
      const match = speakerNames.find((s) => {
        const b = firstAndLast(s);
        return b.first === a.first && b.last === a.last;
      });
      if (match) return speakerClassToHex(speakerColors[match]);
    }
  }

  // 3. Match email local-part to a speaker's first name
  //    (e.g. "aruna" → "Aruna Neervannan", "victor.gonzales" → "Victor Gonzales")
  if (emailLocal) {
    const cleaned = norm(emailLocal.replace(/[._-]+/g, " "));
    const target = cleaned.split(/\s+/).filter(Boolean);
    if (target.length > 0) {
      const match = speakerNames.find((s) => {
        const b = firstAndLast(s);
        // first-name only match, or first+last match against email parts
        return (
          (target.length === 1 && b.first === target[0]) ||
          (target.length > 1 && b.first === target[0] && b.last === target[target.length - 1])
        );
      });
      if (match) return speakerClassToHex(speakerColors[match]);
    }
  }

  return undefined;
}

const COLORS_OBJECT = ['#14bb7f', '#ff9900', '#ff4d97', '#917fd8', '#36b7d3', '#298ce8', '#362bb0', '#bb6bd9', '#dc47c4', '#f68e6d', '#63831e', '#a8b700', '#5094ba', '#00695c', '#4f37e3', '#0052cc', '#2d323c', '#002d52','#96b2c9','#334252','#ff4444','#ffbb33','#00C851','#33b5e5','#cc0000','#007e33','#ff8800','#0099cc','#2bbbad','#4285f4','#aa66cc','#00695c','#0d47a1','#9933cc','#4a148c']

export const getColorsForArray = (arraySize: number = 0) => COLORS_OBJECT.slice(0, arraySize);

const development: boolean = process.env.REACT_APP_STAGE === 'development';

export function isDev(): boolean {
  return development;
}
export function visibility(): "hidden" | "-moz-initial" | "inherit" | "initial" | "revert" | "unset" | "collapse" | "visible" | undefined {
  return "visible"
}
export function disabled(): boolean {
  return !development;
}

export const getItemElement = (id: string): Element | null => {
  let el: Element | null = null;
  if (id) {
    el = document.querySelector(
      `[id="${id}" ]`
    );
  }
  return el;
}
export function scrollToView(id: string): boolean {

  let e: Element | null = getItemElement(id);
  if (e !== null) {
    e.scrollIntoView({ behavior: "smooth" });
    return true;
  }
  return false;
}

export function generateColor(): string {
  return "#" + Math.random().toString(16).substr(-6);
}
export function randDarkColor(colors: string[]): string {
  let color: string = "";
  while (color.length === 0 || colors.indexOf(color) > -1) {
    const rand: number = Math.ceil(Math.random() * 3);
    let red: number = 1;
    let green: number = 1;
    let blue: number = 1;
    let seed: number = 255;
    if (rand === 1) {
      seed = Math.ceil(Math.random() * 255);
      red = 1;
      green = .5;
      blue = .25;
    } else if (rand === 2) {
      seed = Math.ceil(Math.random() * 200);
      red = .5;
      green = 1;
      blue = .25;
    } else if (rand === 3) {
      seed = Math.ceil(Math.random() * 255);
      red = .25;
      green = .50;
      blue = 1;
    }
    const r: number = Math.floor(red * seed);
    const g: number = Math.floor(green * seed);
    const b: number = Math.floor(blue * seed);
    color = `rgb(${r},${g},${b})`;
  }
  colors.push(color);
  return color;
}
export const getAttendees = (meeting: IMeeting) => {
  return meeting.attendees ? meeting.attendees.length : 0;
}
export function groupByCategory(data: ITranscriptInfo[]): any {
  if (data == null) { return []; }
  return data.reduce(function (category: DictionaryItems<ITranscriptInfo>, item: ITranscriptInfo): any {
    if (item.subTopics) {
      item.subTopics && item.subTopics.forEach(chunk => {
        if (chunk) {
          (category[chunk] = category[chunk] || []).push(item);
        }
      })
    }
    item.trackers && item.trackers.forEach(chunk => {
      if (chunk) {
        (category[chunk] = category[chunk] || []).push(item);
      }
    });
    item.chunkCategories && item.chunkCategories.forEach(chunk => {
      if (chunk && chunk.category !== null) {
        (category[chunk.category] = category[chunk.category] || []).concat(item);
      }
    });
    return category;
  }, {});
}

export function groupByTracker(data: ITranscriptInfo[]): any {
  if (data == null) { return []; }
  return data.reduce(function (category: DictionaryItems<ITranscriptInfo>, item: ITranscriptInfo): any {
    item.subTopics && item.subTopics.forEach(chunk => {
      if (chunk) {
        (category[chunk] = category[chunk] || []).push(item);
      }
    });
    item.trackers && item.trackers.forEach(chunk => {
      if (chunk) {
        (category[chunk] = category[chunk] || []).concat(item);
      }
    });

    item.chunkCategories && item.chunkCategories.forEach(chunk => {
      if (chunk && chunk.category !== null) {
        (category[chunk.category] = category[chunk.category] || []).concat(item);
      }
    });
    return category;
  }, {});
}

export function groupByTopics(topicRules: any, subtopics: DictionaryItems<any>, subtracker, data: ITranscriptInfo[]): any {
  if (!topicRules) return subtopics;
  let topics: DictionaryItem<DictionaryItems<ITranscriptInfo[]>> = {};
  if (subtopics == null) { return []; }
  topicRules.forEach(item => {
    let topic = topics[item.name] = topics[item.name] || {};
    if (item.topics) {
      item.topics.forEach(key => {
        let subtopic = topic[key] = topic[key] || [];
        if (subtopics[key]) {
          subtopic.push(...subtopics[key])
        }
      })
    }
  })
  return topics;
}

export function getTopicCounts(arr: DictionaryItems<ITranscriptInfo[]>, subtopic_counts:
  (getFullMeetingById_meeting_analytics_subtopic_count | null)[] | null | undefined): TopicCounts {
  let mentionCount: number = 0;
  let topicCount: number = 0;
  let percentageSum: number = 0;
  let durationSum: number = 0;

  if (subtopic_counts) {
    Object.keys(arr).forEach((key: string) => {
      let subtopic = subtopic_counts.find((x) => x?.name === key);
      if (subtopic) {
        let mention = (subtopic.mentions ? subtopic?.mentions : 0);
        if (mention) {
          topicCount++;
          percentageSum = percentageSum + (subtopic.percentage ? subtopic?.percentage : 0);
          durationSum = durationSum + (subtopic.duration ? subtopic?.duration : 0);
        }
        mentionCount = mentionCount + mention;
      }
    });
  }
  return { topicCount: topicCount, mentionCount: mentionCount, percentageSum: percentageSum, durationSum: durationSum };
}

export function getTopicCount(arr: DictionaryItems<ITranscriptInfo[]>, subtopic_counts:
  (getFullMeetingById_meeting_analytics_subtopic_count | null)[] | null | undefined): number {
  let topicCount: number = 0;
  if (subtopic_counts) {
    Object.keys(arr).forEach((key: string) => {
      let subtopic = subtopic_counts.find((x) => x?.name === key);
      if (subtopic) {
        let mention = (subtopic.mentions ? subtopic?.mentions : 0);
        if (mention) {
          topicCount++;
        }
      }
    });
  }
  return topicCount;
}

export function randColor(colors: string[]): string {
  let color: string = "";
  while (color.length === 0 || colors.indexOf(color) > -1) {
    const rand: number = Math.ceil(Math.random() * 12);
    color = COLORS[rand];
    if (colors.length >= 13) {
      color = "balck"
      break;
    }
  }
  colors.push(color);
  return color;
}

/** Shared color palette for topics & trackers across all views */
export const TOPIC_TRACKER_COLORS = [
  '#7C3AED', '#2563EB', '#059669', '#D97706', '#DC2626',
  '#DB2777', '#4F46E5', '#0891B2', '#65A30D', '#9333EA',
];

/** Build a deterministic name → hex color map from transcript topics/trackers (by first appearance) */
export function buildTopicTrackerColorMap(transcripts: ITranscriptInfo[]): DictionaryItem<string> {
  const colorMap: DictionaryItem<string> = {};
  let colorIdx = 0;
  (transcripts || []).forEach((t) => {
    t.topics?.forEach((name) => {
      if (name && name !== "" && !colorMap[name]) {
        colorMap[name] = TOPIC_TRACKER_COLORS[colorIdx % TOPIC_TRACKER_COLORS.length];
        colorIdx++;
      }
    });
    t.trackers?.forEach((name) => {
      if (name && name !== "" && !colorMap[name]) {
        colorMap[name] = TOPIC_TRACKER_COLORS[colorIdx % TOPIC_TRACKER_COLORS.length];
        colorIdx++;
      }
    });
  });
  return colorMap;
}

export function getColor(index: number): string {
  if (index < COLORS.length) {
    return COLORS[index];
  }
  return "black";
}
export function getCategoryColors(nodes: DictionaryItems<any>): DictionaryItem<string> {
  const items: DictionaryItem<string> = {};
  if (nodes == null) {
    return items;
  }
  Object.keys(nodes).forEach((s, index) => { items[s] = getColor(index); });
  return items;
}

export function getColors(nodes: any[], key: string): DictionaryItem<string> {
  const items: DictionaryItem<string> = {};
  if (nodes == null) {
    return items;
  }
  const groups: string[] = findUniques(nodes, key);
  groups.forEach((s, index) => { items[s] = getColor(index); });
  return items;
}

// OpportunityResult.representative,  then setColors({},OpportunityResult,undefined,"representative")
// OpportunityResult.contacts.contactName,  then  setColors({},OpportunityResult,"contacts","contactName")
export function setColors(colorList:DictionaryItem<string>, nodes: any[] | null | undefined, objectName:string| undefined,key: string): DictionaryItem<string> {
  if (!nodes) {
    return colorList;
  }
  let groups: string[] = [];
  if(objectName) {
    groups = findUniquesByKey(nodes, objectName, key);
  }
  else{
    groups = findUniques(nodes, key);
  }
  let colorIndex = Object.keys(colorList).length;
  if(colorIndex > COLORS.length) {
    colorIndex = 0;
  }
  groups.forEach((s, index) => { 
    if(!colorList[s]) {
      colorList[s] = getColor(colorIndex); 
      colorIndex++;
    }
    if(colorIndex > COLORS.length) {
      colorIndex = 0;
    }
  });
  return colorList;
}

export function toTranscriptInput(selectedTranscript: any): TranscriptInfoInput {
  let transcripts: TranscriptInfoInput = {
    id: selectedTranscript.id,
    start: selectedTranscript.start,
    end: selectedTranscript.end,
    name: selectedTranscript.name,
    text: selectedTranscript.text,
    chunkCategories: selectedTranscript.chunkCategories,
    subTopics: selectedTranscript.subTopics,
    trackers: selectedTranscript.trackers
  };
  return transcripts;
}


export const omitDeep = (value, key) => {
  if (Array.isArray(value)) {
    return value.map(i => omitDeep(i, key));
  } else if (typeof value === 'object' && value !== null) {
    return Object.keys(value).reduce((newObject, k) => {
      if (k === key || k === "id") return newObject;
      return Object.assign({ [k]: omitDeep(value[k], key) }, newObject);
    }, {});
  }
  return value;
};

export const copyDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(i => copyDeep(i));
  } else if (typeof value === 'object' && value !== null) {
    return Object.keys(value).reduce((newObject, k) => {
      return Object.assign({ [k]: copyDeep(value[k]) }, newObject);
    }, {});
  }
  return value;
};

export function toMeetingtInput(selectedMeeting: any): MeetingInput {
  return omitDeep(selectedMeeting, "__typename");

}

export function toCustomerInput(selectedCustomer: any): CustomerInfo {
  return omitDeep(selectedCustomer, "__typename");

}

export const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const buildURI = ((data, uFEFF, headers, separator, enclosingCharacter) => {
  const type = isSafari() ? 'application/csv' : 'text/csv';
  const blob = new Blob([uFEFF ? '\uFEFF' : '', data], {type});
  const dataURI = `data:${type};charset=utf-8,${uFEFF ? '\uFEFF' : ''}${data}`;

  const URL = window.URL || window.webkitURL;

  return (typeof URL.createObjectURL === 'undefined')
    ? dataURI
    : URL.createObjectURL(blob);
});

export function querystring(name, url = window.location.href) {
  name = name.replace(/[[]]/g, "\\$&");

  const regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)", "i");
  const results = regex.exec(url);

  if (!results) {
    return null;
  }
  if (!results[2]) {
    return "";
  }

  return decodeURIComponent(results[2].replace(/\+/g, " "));
}



export const isWebEnabled = (user) => {
  return (user.customer?.providers?.filter(item => item.service === "Web Conferencing")?.length > 0 ||
                              user?.providers?.filter(item => item.service === "Web Conferencing")?.length > 0)
}

export const isDialerEnabled = (user) => {
  return user?.providers?.filter(item => item?.service?.toLowerCase() === "dialer")?.length > 0;
}

export const isCrmEnabled = (user) => {
  return user.customer?.providers?.filter(item => item?.service?.toLowerCase() === "crm")?.length > 0;
}
export const isCalendarEnabled = (user) => {
  return user.providers?.filter(item => item?.service?.toLowerCase() === "calendar")?.length > 0
}

export const isUserComplate = (user) => {
  return (user.firstName && user.lastName && user.designation && user.phone && user.bioMetricUrl)
}